import { Filters } from '@io/domain';
import { AnalyticsFormControl } from '@io/domain/analytics-form-control';
import { AnalyticsService } from '@io/services';
import { DropDownOptionBuilder } from '@io/builder';
import { AnalyticsClickDirective } from '@io/directives';
import { LtvCalculationComponent } from '@io/application/ltv-calculation/ltv-calculation.component';
import { LoanToValuePipe } from '@io/transformer/loan-to-value/loan-to-value-pipe';

import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { merge } from 'rxjs';
import { NgTemplateOutlet } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';

import { AmortisationType, EnergyLabel, FixedRatePeriod, LoanToValue } from '@ap/ap-interface-apix-rs-hypotheken-renteoverzicht';
import { ApDropdownComponent, DropdownOption } from '@ap/dropdown';
import { ApContentComponent, ApContentPipe, ApContentService } from '@ap/content';
import { ApChipComponent, ApChipListComponent } from '@ap/chip';
import { ApPopoverComponent } from '@ap/popover';
import { ApIconComponent } from '@ap/icon';

@Component({
    selector: 'filters',
    standalone: true,
    imports: [
        ApContentPipe,
        LoanToValuePipe,
        ApChipComponent,
        ApChipListComponent,
        LtvCalculationComponent,
        NgTemplateOutlet,
        ApPopoverComponent,
        ApContentComponent,
        ApIconComponent,
        ApDropdownComponent,
        ReactiveFormsModule,
        AnalyticsClickDirective
    ],
    templateUrl: './filters.component.html'
})
export class FiltersComponent implements OnInit {
    @Output()
    filters: EventEmitter<Filters> = new EventEmitter<Filters>();

    private readonly analyticsService = inject(AnalyticsService);
    private readonly contentService = inject(ApContentService);

    amortisationTypeList: DropdownOption[] = [];
    energyLabelList: DropdownOption[] = [];
    fixedRatePeriodList: DropdownOption[] = [];

    loanToValueList: LoanToValue[] = [];

    amortisationType: AnalyticsFormControl<AmortisationType>;
    fixedRatePeriod: AnalyticsFormControl<FixedRatePeriod>;
    loanToValue: AnalyticsFormControl<LoanToValue>;
    energyLabel: AnalyticsFormControl<EnergyLabel>;

    ngOnInit(): void {
        this.amortisationType = new AnalyticsFormControl<AmortisationType>(
            this.analyticsService,
            'hypotheekvorm',
            AmortisationType.ANNUITY
        );
        this.fixedRatePeriod = new AnalyticsFormControl<FixedRatePeriod>(this.analyticsService, 'rentevaste periode', FixedRatePeriod.TEN);
        this.loanToValue = new AnalyticsFormControl<LoanToValue>(this.analyticsService, 'tariefgroep', LoanToValue.NHG);
        this.energyLabel = new AnalyticsFormControl<EnergyLabel>(this.analyticsService, 'energielabel', EnergyLabel.A);

        this.amortisationTypeList = this.getAmortisationTypeList();
        this.energyLabelList = this.getEnergyLabelList();
        this.fixedRatePeriodList = this.getFixedRatePeriodList();

        this.loanToValueList = this.getLoanToValueList();
        this.filters.emit(
            new Filters(this.amortisationType.value, this.fixedRatePeriod.value, this.loanToValue.value, this.energyLabel.value)
        );
        merge(
            this.amortisationType.valueChanges,
            this.fixedRatePeriod.valueChanges,
            this.energyLabel.valueChanges,
            this.loanToValue.valueChanges
        ).subscribe(() => {
            this.filters.emit(
                new Filters(this.amortisationType.value, this.fixedRatePeriod.value, this.loanToValue.value, this.energyLabel.value)
            );
        });
    }

    setLoanToValue(loanToValue: LoanToValue): void {
        this.loanToValue.setValue(loanToValue);
    }

    selectChip(loanToValue: LoanToValue): boolean {
        return this.loanToValue.value === loanToValue;
    }

    private getAmortisationTypeList(): DropdownOption[] {
        return Object.keys(AmortisationType).map((amortisationType) =>
            new DropDownOptionBuilder()
                .withValue(AmortisationType[amortisationType])
                .withLabel(this.getContentAmortisationType(this.transformAmortisationTypeToString(AmortisationType[amortisationType])))
                .build()
        );
    }

    private transformAmortisationTypeToString(amortisationType: AmortisationType): string {
        switch (amortisationType) {
            case AmortisationType.ANNUITY:
                return 'annuity';
            case AmortisationType.LINEAR:
                return 'linear';
            case AmortisationType.INTEREST_ONLY:
                return 'interest-only';
            default:
                return 'annuity';
        }
    }

    private getContentAmortisationType(amortisationType: string): string {
        return this.contentService.get(`${amortisationType}.amortisation-type.option`);
    }

    private getEnergyLabelList(): DropdownOption[] {
        return Object.keys(EnergyLabel).map((energyLabel) =>
            new DropDownOptionBuilder()
                .withValue(EnergyLabel[energyLabel])
                .withLabel(this.getContentEnergyLabel(this.transformEnergyLabelToString(EnergyLabel[energyLabel])))
                .build()
        );
    }

    private transformEnergyLabelToString(energyLabelPipe: EnergyLabel): string {
        switch (energyLabelPipe) {
            case EnergyLabel.APPPP:
                return 'apppp';
            case EnergyLabel.APPP:
                return 'appp';
            case EnergyLabel.APP:
                return 'app';
            case EnergyLabel.AP:
                return 'ap';
            case EnergyLabel.A:
                return 'a';
            case EnergyLabel.B:
                return 'b';
            case EnergyLabel.C:
                return 'c';
            case EnergyLabel.D:
                return 'd';
            case EnergyLabel.E:
                return 'e';
            case EnergyLabel.F:
                return 'f';
            case EnergyLabel.G:
                return 'g';
            default:
                return 'unknown';
        }
    }

    private getContentEnergyLabel(energyLabel: string): string {
        return this.contentService.get(`energy-label-${energyLabel}.option`);
    }

    private getFixedRatePeriodList(): DropdownOption[] {
        return Object.keys(FixedRatePeriod)
            .map((fixedRatePeriod) => FixedRatePeriod[fixedRatePeriod])
            .map((fixedRatePeriod) =>
                new DropDownOptionBuilder()
                    .withValue(fixedRatePeriod)
                    .withLabel(this.getContentFixedRatePeriod(this.transformFixedRatePeriodToString(fixedRatePeriod)))
                    .build()
            );
    }

    private getLoanToValueList(): LoanToValue[] {
        return Object.keys(LoanToValue).map((loanToValue) => LoanToValue[loanToValue]);
    }

    private transformFixedRatePeriodToString(fixedRatePeriod: FixedRatePeriod): string {
        switch (fixedRatePeriod) {
            case FixedRatePeriod.VARIABLE:
                return 'variable';
            case FixedRatePeriod.ONE:
                return 'one';
            case FixedRatePeriod.TWO:
                return 'two';
            case FixedRatePeriod.THREE:
                return 'three';
            case FixedRatePeriod.FOUR:
                return 'four';
            case FixedRatePeriod.FIVE:
                return 'five';
            case FixedRatePeriod.SIX:
                return 'six';
            case FixedRatePeriod.SEVEN:
                return 'seven';
            case FixedRatePeriod.TEN:
                return 'ten';
            case FixedRatePeriod.TWELVE:
                return 'twelve';
            case FixedRatePeriod.FIFTEEN:
                return 'fifteen';
            case FixedRatePeriod.TWENTY:
                return 'twenty';
            default:
                return 'ten';
        }
    }

    private getContentFixedRatePeriod(fixedRatePeriod: string): string {
        return this.contentService.get(`${fixedRatePeriod}.fixed-rate-period.option`);
    }
}
